import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { companyAlerts as alerts, useAlert } from 'shared/components/alerts';
import { useUpdateCompany } from 'hq/hooks/queryHooks/companies/use-update-company';
import { useHqRelativePath } from 'hq/hooks/useHqRelativePath';
import { invariant } from 'shared/utils/utils';
import { RenderUnsettledUI } from 'shared/components';
import { FormProvider, useForm } from 'react-hook-form';
import { FormEvent, useCallback } from 'react';
import { Box } from '@mui/material';
import { Header, LwButton, Page, Toolbox } from 'redesign';
import { useGetCertificateNames } from 'hq/components/views/accreditations/hooks/use-get-certificates-names';
import { HQCertificateWithFileUrl } from 'hq/services/accreditations-service.types';
import { CompanyForm, CompanyFormData } from './company-form';
import { companyDataPrepareForSubmit } from './utils';
import { routes } from '../../../../../../Routes';
import { useGetCompanyAsDetail } from '../../../../../hooks/queryHooks/companies/use-get-company-detail';
import { CompanyDetailSelectModel } from '../../../../../services/mappers/mappers';
import { IconApproved, IconCancel } from '../../../../../../assets/img';

export const CompanyEdit = () => {
  const { id } = useParams<'id'>();
  invariant(id, 'Company ID not provided');
  const companyQuery = useGetCompanyAsDetail(id);
  const certificatesQuery = useGetCertificateNames();
  const { search: filterParam } = useLocation();

  if (companyQuery.status !== 'success') {
    return <RenderUnsettledUI data={companyQuery} />;
  }

  if (certificatesQuery.status !== 'success') {
    return <RenderUnsettledUI data={certificatesQuery} />;
  }

  return (
    <CompanyEditImpl
      previousData={companyQuery.data}
      companyId={id}
      certificates={certificatesQuery.data}
      filterParam={filterParam}
    />
  );
};

const CompanyEditImpl = ({
  previousData,
  companyId,
  certificates,
  filterParam,
}: {
  previousData: CompanyDetailSelectModel;
  companyId: string;
  certificates: HQCertificateWithFileUrl[];
  filterParam: string;
}) => {
  const generatePath = useHqRelativePath();

  const formMethods = useForm<CompanyFormData>({
    defaultValues: {
      name: previousData.name,
      offersTempWork: previousData.offersTempWork,
      offersFreelanceWork: previousData.offersFreelanceWork,
      offersEmployeeWork: previousData.offersEmployeeWork,
      workCooldownMinutes: previousData.workCooldownMinutes,
      receiptCertificates: previousData.receiptCertificates,
      maxFLTravelAllowance:
        previousData.maxFLTravelAllowanceCents != null
          ? previousData.maxFLTravelAllowanceCents / 100
          : undefined,
      maxParking:
        previousData.maxParkingCents != null ? previousData.maxParkingCents / 100 : undefined,
      maxTWTravelAllowance:
        previousData.maxTWTravelAllowanceCents != null
          ? previousData.maxTWTravelAllowanceCents / 100
          : undefined,
      maxMileageAllowance: previousData.maxMileageAllowanceMeters ?? undefined,
    },
  });

  const { alertSuccess, alertError } = useAlert();
  const navigate = useNavigate();

  const updateCompany = useUpdateCompany(previousData.id);
  const redirectPath = generatePath(routes.CompanyDetail, { id: companyId }) + filterParam;
  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();

      formMethods.handleSubmit((data) => {
        const processedData = companyDataPrepareForSubmit(data);
        updateCompany.mutate(processedData, {
          onSuccess: () => {
            alertSuccess(alerts.success.updated);
            navigate(redirectPath);
          },
          onError: (e) => alertError(e),
        });
      })();
    },
    [previousData]
  );

  const cancelRedirectPath =
    generatePath(routes.CompanyDetail, { id: previousData.id }) + filterParam;

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit}>
        <Page
          header={<Header titleText="Bedrijf bewerken" />}
          toolbox={
            <Toolbox>
              <LwButton
                type="submit"
                iconColorMode="stroke"
                color="primary"
                startIcon={<IconApproved />}
              >
                Bedrijf aanpassen
              </LwButton>
              <LwButton
                onClick={() => navigate(cancelRedirectPath)}
                iconColorMode="fill"
                color="secondary"
                startIcon={<IconCancel />}
              >
                Annuleer
              </LwButton>
            </Toolbox>
          }
        >
          <Box width="100%">
            <CompanyForm state="update" certificates={certificates} />
          </Box>
        </Page>
      </form>
    </FormProvider>
  );
};
